import { useState } from 'react';
import { errorMessage, successMessage } from '~/utils/toasts';
import Modal from '~/components/common/modal';
import { Section } from '~/components/common/section';
import { CloseIcon } from '~/svgs';
import OTPVerify from '~/components/common/otp-verify-form';
import { sentryCaptureException } from '~/errors/sentry';
import { bnplChangeState } from '~/apis/bnpl/bnblChangeState';
import { bnplVerifyOtp } from '~/apis/bnpl/bnplVerifyOtp';
import { useAuth } from '~/auth';
import { Translate } from '~/i18n';
import { DoneOutlineIcon } from '~/svgs';
import useUserProfile from '~/apis/useUserProfile';

type BnplOtpModalType = {
   open: boolean;
   onClose: () => any;
   isBnplActive?: boolean;
};

type BnplOptionsProps = {
   tabby: boolean;
   tamara: boolean;
};

const BnplOtpModal = ({
   open,
   onClose,
   isBnplActive,
   tabby: tabby_active,
   tamara: tamara_active,
}: BnplOtpModalType & BnplOptionsProps) => {
   const { user, updateUser } = useAuth();
   const [error, setError] = useState('');
   const [loading, setLoading] = useState(false);
   const [bnplStatusChangeDone, setBnplStatusChangeDone] = useState(false);
   const [bnplNewState, setBnplNewState] = useState(false);
   const { refetch: refetchUser } = useUserProfile({
      onSuccess: data => {
         if (data && data.status) {
            updateUser({ ...user, ...data });
         }
      },
   });
   const submitOTP = async (smsCode: string) => {
      if (!smsCode) return;
      try {
         setLoading(true);
         const { response, error } = await bnplVerifyOtp(smsCode, tabby_active, tamara_active);
         if (error) {
            setLoading(false);
            sentryCaptureException(error, { tags: { journey: 'business' } });
            errorMessage(error?.response?.data?.data, 4000);
         }
         if (response) {
            setLoading(false);
            setBnplNewState(!isBnplActive);
            updateUser({
               ...user,
               isBnplEnabled: !isBnplActive,
            });
            refetchUser();
            setBnplStatusChangeDone(true);
         }
      } catch (error: any) {
         sentryCaptureException(error, { tags: { journey: 'profile' } });
         if (error.response?.status === 422) {
            Object.values(error.response?.data?.errors).map((error: any) => {
               setError(error);
            });
         }
         console.info(error);
         setLoading(false);
      }
   };

   const resendCode = async () => {
      try {
         setLoading(true);
         const { response, error } = await bnplChangeState({
            bnpl_agreed: Boolean(tabby_active || tamara_active) === true ? true : false,
            tabby_active,
            tamara_active,
         });
         if (error) {
            setLoading(false);
            sentryCaptureException(error, { tags: { journey: 'business' } });
            errorMessage(error?.response?.data?.message, 4000);
         }
         if (response) {
            setLoading(false);
            successMessage('code sent');
         }
      } catch (error: any) {
         sentryCaptureException(error, { tags: { journey: 'profile' } });
         console.info(error);
         setLoading(false);
      }
   };

   return (
      <Modal size="xs" open={open}>
         <Modal.Body>
            <Section className="p-2 pb-10 ">
               <div className="flex justify-end w-full">
                  <button
                     className="mx-6 mt-6"
                     onClick={() => {
                        setBnplStatusChangeDone(false);
                        onClose();
                     }}>
                     <CloseIcon />
                  </button>
               </div>
               {bnplStatusChangeDone ? (
                  <RequestDoneAlert isBnplActive={bnplNewState} />
               ) : (
                  <OTPVerify
                     loading={loading}
                     phoneNumber={user?.current_user?.mobile}
                     close={() => {
                        setBnplStatusChangeDone(false);
                        onClose();
                     }}
                     resendCode={resendCode}
                     submitOTP={code => submitOTP(code)}
                     error={error}
                  />
               )}
            </Section>
         </Modal.Body>
      </Modal>
   );
};

const RequestDoneAlert = ({ isBnplActive }: { isBnplActive?: boolean }) => {
   return (
      <div className="flex flex-col items-center px-8 space-y-4 text-center">
         <DoneOutlineIcon width={45} height={45} className="mb-3 fill-primary" />
         <div className="my-2 text-xl font-bold capitalize">
            <Translate
               id={`bnpl:${
                  !isBnplActive ? 'bnpl is now inactive title' : 'bnpl is now active title'
               }`}
            />
         </div>
         <div className="text-grey-100">
            <Translate id="bnpl:bnpl enable or disable settings" />
         </div>
      </div>
   );
};

export default BnplOtpModal;
